import type {ComponentPropsWithoutRef, ElementType} from 'react';
import React, {useRef, useState} from 'react';
import type {OverlayScrollbars} from 'overlayscrollbars';
import styles from './scroll.component.module.styl';
import {withCssModule} from '@pb/kit-util-with-css-module';
import type {ScrollProps} from './scroll.component';
import {Scroll} from './scroll.component';
import {ScrollArrow} from './scroll-arrow.component';
import mergeRefs from 'merge-refs';

const classes = withCssModule(styles);

type NativeProps = ComponentPropsWithoutRef<'div'>;

export interface ScrollArrowsProps<T extends ElementType = 'div'> extends NativeProps {
    scrollStep?: number,
    as?: T,
    scrollProps?: ScrollProps,
}

export function ScrollArrows({
    as: Slug = 'div',
    children,
    className,
    scrollProps,
    scrollStep = 100,
    ...restProps
}: ScrollArrowsProps) {
    const [isOnLeftPosition, setIsOnLeftPosition] = useState(false);
    const [isOnRightPosition, setIsOnRightPosition] = useState(false);
    const [hasScroll, setHasScroll] = useState(false);
    const ref: React.MutableRefObject<OverlayScrollbars | null> = useRef(null);
    function updatePositions(scrollInstance: OverlayScrollbars) {
        const scrollLeft = scrollInstance.elements().viewport.scrollLeft;
        const scrollWidth = scrollInstance.state().overflowAmount.x;

        setIsOnLeftPosition(!scrollLeft || scrollLeft < 1);
        setIsOnRightPosition(!!(scrollLeft && scrollWidth && scrollLeft > scrollWidth - 1));
    }

    function updateState(scrollInstance: OverlayScrollbars) {
        setHasScroll(scrollInstance.state().hasOverflow.x);
        updatePositions(scrollInstance);
    }

    return (
        <Slug
            {...restProps}
            className={classes('scroll-arrow-container', className)}>
            <Scroll
                {...scrollProps}
                _ref={mergeRefs(
                    scrollProps?._ref,
                    scrollref => {
                        ref.current = scrollref?.osInstance() || null;
                    })}
                events={{
                    initialized: updateState,
                    updated: updateState,
                    scroll: updatePositions,
                }}>
                {children}
            </Scroll>
            {hasScroll && !isOnLeftPosition ? (
                <ScrollArrow direction="left" getScrollInstance={() => ref.current} scrollStep={scrollStep}/>
            ) : null}
            {hasScroll && !isOnRightPosition ? (
                <ScrollArrow direction="right" getScrollInstance={() => ref.current} scrollStep={scrollStep}/>
            ) : null}
        </Slug>
    );
}
